<!--
- SPDX-FileCopyrightText: 2025 Nextcloud GmbH and Nextcloud contributors
- SPDX-License-Identifier: AGPL-3.0-or-later
-->

<script setup lang="ts">
import {
	showError,
} from '@nextcloud/dialogs'
import { t } from '@nextcloud/l10n'
import { useResizeObserver } from '@vueuse/core'
import debounce from 'debounce'
import { computed, onMounted, onUnmounted, ref, toValue, useTemplateRef } from 'vue'
import { useStore } from 'vuex'
import NcActionButton from '@nextcloud/vue/components/NcActionButton'
import NcActions from '@nextcloud/vue/components/NcActions'
import NcActionSeparator from '@nextcloud/vue/components/NcActionSeparator'
import NcButton from '@nextcloud/vue/components/NcButton'
import NcLoadingIcon from '@nextcloud/vue/components/NcLoadingIcon'
import IconChevronUp from 'vue-material-design-icons/ChevronUp.vue'
import IconFullscreen from 'vue-material-design-icons/Fullscreen.vue'
import IconFullscreenExit from 'vue-material-design-icons/FullscreenExit.vue'
import IconSubtitles from 'vue-material-design-icons/Subtitles.vue'
import IconSubtitlesOutline from 'vue-material-design-icons/SubtitlesOutline.vue'
import IconViewDashboardOutline from 'vue-material-design-icons/ViewDashboardOutline.vue'
import IconViewGalleryOutline from 'vue-material-design-icons/ViewGalleryOutline.vue'
import IconViewGridOutline from 'vue-material-design-icons/ViewGridOutline.vue'
import ReactionMenu from '../TopBar/ReactionMenu.vue'
import TopBarMediaControls from '../TopBar/TopBarMediaControls.vue'
import CallEndLeaveButton from './CallEndLeaveButton.vue'
import {
	toggleFullscreen,
	useDocumentFullscreen,
} from '../../composables/useDocumentFullscreen.ts'
import { useGetToken } from '../../composables/useGetToken.ts'
import { ATTENDEE, CONVERSATION } from '../../constants.ts'
import {
	getTalkConfig,
	showTalkFeatureHint,
} from '../../services/CapabilitiesManager.ts'
import { useActorStore } from '../../stores/actor.ts'
import { useCallViewStore } from '../../stores/callView.ts'
import { useLiveTranscriptionStore } from '../../stores/liveTranscription.ts'
import { useSettingsStore } from '../../stores/settings.ts'
import { isClassifiedConversation } from '../../utils/conversation.ts'
import { localCallParticipantModel, localMediaModel } from '../../utils/webrtc/index.js'

const { isSidebar = false } = defineProps<{
	isSidebar: boolean
}>()

const store = useStore()
const token = useGetToken()
const actorStore = useActorStore()
const isFullscreen = !isSidebar && useDocumentFullscreen()
const callViewStore = useCallViewStore()
const liveTranscriptionStore = useLiveTranscriptionStore()
const settingsStore = useSettingsStore()

const isLiveTranscriptionLoading = ref(false)
const bottomBar = useTemplateRef('bottomBar')
const callButtonWithActions = useTemplateRef('callButtonWithActions')

const conversation = computed(() => {
	return store.getters.conversation(token.value) || store.getters.dummyConversation
})
const isGuestActor = computed(() => actorStore.actorType === ATTENDEE.ACTOR_TYPE.GUESTS)
const isVoiceRoom = computed(() => Boolean(conversation.value.attributes & CONVERSATION.ATTRIBUTE.VOICE_ROOM))

// Live transcription and translation are rejected by the backend in classified conversations
const isClassified = computed(() => isClassifiedConversation(conversation.value))
const isLiveTranscriptionSupported = computed(() => (getTalkConfig(token.value, 'call', 'live-transcription') || false) && !isClassified.value)
const hintTranscriptionSupported = computed(() => !isLiveTranscriptionSupported.value && showTalkFeatureHint(34) && !isClassified.value)
const isLiveTranslationSupported = computed(() => (getTalkConfig(token.value, 'call', 'live-translation') || false) && !isClassified.value)
const hintTranslationSupported = computed(() => !isLiveTranslationSupported.value && showTalkFeatureHint(34) && !isClassified.value)

const liveTranscriptionButtonLabel = computed(() => {
	if (callViewStore.isLiveTranscriptionEnabled && languageType.value === LanguageType.Original) {
		return t('spreed', 'Disable live transcription')
	}

	return t('spreed', 'Enable live transcription (generated by AI)')
})

const hintTranscriptionButtonLabel = computed(() => {
	return t('spreed', 'Enable live transcription (Live transcriptions app is not installed)')
})

const liveTranslationButtonLabel = computed(() => {
	if (callViewStore.isLiveTranscriptionEnabled && languageType.value === LanguageType.Target) {
		return t('spreed', 'Disable live translation')
	}

	return t('spreed', 'Enable live translation (generated by AI)')
})

const hintOriginalButtonLabel = computed(() => {
	return t('spreed', 'Original language')
})

const hintTranslationButtonLabel = computed(() => {
	return t('spreed', 'Live translations app is not installed')
})

const originalLanguageButtonLabel = computed(() => {
	const languageId = conversation.value.liveTranscriptionLanguageId || 'en'

	const languageName = liveTranscriptionStore.getLiveTranscriptionLanguages()?.[languageId]?.name ?? languageId

	return t('spreed', 'Original language: {languageName}', {
		languageName,
	})
})

const targetLanguageButtonLabel = computed(() => {
	const languageId = targetLanguageId.value
	if (!languageId) {
		return t('spreed', 'Translated language')
	}

	const languageName = liveTranscriptionStore.getLiveTranscriptionTargetLanguages()?.[languageId]?.name ?? languageId

	return t('spreed', 'Translated language: {languageName}', {
		languageName,
	})
})

const targetLanguageId = computed(() => {
	const languageId = settingsStore.liveTranscriptionTargetLanguageId

	if (languageId) {
		return languageId
	}

	return liveTranscriptionStore.getLiveTranscriptionDefaultTargetLanguageId()
})

const targetLanguageAvailable = computed(() => {
	const liveTranscriptionTargetLanguages = liveTranscriptionStore.getLiveTranscriptionTargetLanguages()

	const liveTranscriptionLanguageId = conversation.value.liveTranscriptionLanguageId || 'en'

	return targetLanguageId.value
		&& targetLanguageId.value !== liveTranscriptionLanguageId
		&& liveTranscriptionTargetLanguages && liveTranscriptionTargetLanguages[targetLanguageId.value]
})

const LanguageType = {
	Original: 'original',
	Target: 'target',
} as const

const languageType = ref<typeof LanguageType[keyof typeof LanguageType]>(LanguageType.Original)

const fullscreenLabel = computed(() => {
	return toValue(isFullscreen)
		? t('spreed', 'Exit full screen (F)')
		: t('spreed', 'Full screen (F)')
})

const showCallLayoutSwitch = computed(() => !isSidebar && !callViewStore.isEmptyCallView)
const isGrid = computed(() => callViewStore.isGrid)

const CALL_LAYOUT = {
	GRID: 'grid',
	SPEAKER: 'speaker',
	MULTI_SPEAKER: 'multi-speaker',
} as const
const CALL_LAYOUT_COMPONENT = {
	[CALL_LAYOUT.GRID]: {
		label: t('spreed', 'Grid view'),
		icon: IconViewGridOutline,
	},
	[CALL_LAYOUT.SPEAKER]: {
		label: t('spreed', 'Speaker view'),
		icon: IconViewGalleryOutline,
	},
	[CALL_LAYOUT.MULTI_SPEAKER]: {
		label: t('spreed', 'Multi-speaker view'),
		icon: IconViewDashboardOutline,
	},
} as const
type CallLayout = typeof CALL_LAYOUT[keyof typeof CALL_LAYOUT]
const callLayouts = [CALL_LAYOUT.GRID, CALL_LAYOUT.SPEAKER, CALL_LAYOUT.MULTI_SPEAKER] as const
const callLayout = computed(() => {
	if (isGrid.value) {
		return CALL_LAYOUT.GRID
	}
	return callViewStore.isMultiSpeaker ? CALL_LAYOUT.MULTI_SPEAKER : CALL_LAYOUT.SPEAKER
})

const COLLAPSIBLE_BUTTONS = ['virtualBackground', 'liveTranscription', 'callLayout', 'fullscreen'] as const
type CollapsibleButtons = Record<typeof COLLAPSIBLE_BUTTONS[number], boolean>
const isActionAvailableMask = computed<CollapsibleButtons>(() => ({
	fullscreen: !isSidebar,
	callLayout: showCallLayoutSwitch.value,
	liveTranscription: !isSidebar && isLiveTranscriptionSupported.value,
	virtualBackground: !isSidebar,
}))
const hidingList = ref<CollapsibleButtons>({ ...isActionAvailableMask.value })
const hasHiddenItems = computed(() => Object.values(hidingList.value).some(Boolean))
const BUTTON_WITH_GAP_WIDTH = 38 // var(--default-clickable-area) + var--default-grid-baseline)
const MINIMAL_MEDIA_CONTROLS_WIDTH = 236 // Minimal width to show media controls properly
/**
 * Adjust the layout of the bottom bar based on the available width.
 *
 */
function adjustLayout() {
	if (!bottomBar.value) {
		return
	}
	// 20px is for side paddings of the bottom bar, 8px is for the gap between the call button and the options
	const availableWidth = bottomBar.value.clientWidth - callButtonWithActions.value!.clientWidth - 28
	if (availableWidth <= MINIMAL_MEDIA_CONTROLS_WIDTH) {
		// Not enough space to show anything, hide all buttons
		COLLAPSIBLE_BUTTONS.forEach((button) => {
			hidingList.value[button as keyof typeof hidingList.value] = true
		})
		return
	}

	const buttonsToRender = Math.floor((availableWidth - MINIMAL_MEDIA_CONTROLS_WIDTH) / BUTTON_WITH_GAP_WIDTH)
	// make the first n buttons visible, hide the rest
	const buttonsToCollapse = COLLAPSIBLE_BUTTONS.filter((button) => isActionAvailableMask.value[button])
	buttonsToCollapse.forEach((button, index) => {
		hidingList.value[button] = index >= buttonsToRender
	})
}

const debounceAdjustLayout = debounce(adjustLayout, 200)

useResizeObserver(bottomBar, () => {
	debounceAdjustLayout()
})

onMounted(() => {
	adjustLayout()
})

onUnmounted(() => {
	debounceAdjustLayout.clear?.()
})

/**
 * Load live transcription and translation languages.
 */
function handleLiveTranscriptionLanguageSelectorOpen() {
	liveTranscriptionStore.loadLiveTranscriptionLanguages()
	liveTranscriptionStore.loadLiveTranscriptionTranslationLanguages()
}

/**
 * Toggle live transcriptions.
 *
 * Live translations are enabled again if needed when live transcriptions are
 * toggled on.
 */
async function toggleLiveTranscriptionAndTranslation() {
	if (isLiveTranscriptionLoading.value) {
		return
	}

	isLiveTranscriptionLoading.value = true

	if (!callViewStore.isLiveTranscriptionEnabled) {
		await enableLiveTranscription()

		if (languageType.value === LanguageType.Target) {
			await enableLiveTranslation()
		}
	} else {
		await disableLiveTranscription()
	}

	isLiveTranscriptionLoading.value = false
}

/**
 * Toggle live transcriptions.
 */
async function toggleLiveTranscription() {
	if (isLiveTranscriptionLoading.value) {
		return
	}

	if (callViewStore.isLiveTranscriptionEnabled && languageType.value === LanguageType.Original) {
		isLiveTranscriptionLoading.value = true

		await disableLiveTranscription()

		isLiveTranscriptionLoading.value = false
	} else {
		await switchToOriginalLanguage()
	}
}

/**
 * Toggle live translations.
 *
 * Disabling live translations disables live transcriptions as well.
 */
async function toggleLiveTranslation() {
	if (isLiveTranscriptionLoading.value) {
		return
	}

	if (callViewStore.isLiveTranscriptionEnabled && languageType.value === LanguageType.Target) {
		isLiveTranscriptionLoading.value = true

		await disableLiveTranscription()

		isLiveTranscriptionLoading.value = false
	} else {
		await switchToTargetLanguage()
	}
}

/**
 * Enable live transcriptions, disabling live translations if they were already
 * enabled.
 */
async function switchToOriginalLanguage() {
	if (isLiveTranscriptionLoading.value) {
		return
	}

	isLiveTranscriptionLoading.value = true

	if (!callViewStore.isLiveTranscriptionEnabled && !(await enableLiveTranscription())) {
		isLiveTranscriptionLoading.value = false

		return
	}

	await disableLiveTranslation()

	isLiveTranscriptionLoading.value = false
}

/**
 * Enable live translations, enabling live transcriptions first if they were not
 * enabled yet.
 */
async function switchToTargetLanguage() {
	if (isLiveTranscriptionLoading.value) {
		return
	}

	isLiveTranscriptionLoading.value = true

	if (!callViewStore.isLiveTranscriptionEnabled && !(await enableLiveTranscription())) {
		isLiveTranscriptionLoading.value = false

		return
	}

	await enableLiveTranslation()

	isLiveTranscriptionLoading.value = false
}

/**
 * Enable live transcriptions.
 */
async function enableLiveTranscription() {
	// Strictly speaking it would be the responsibility of the components using
	// the language metadata to ensure that it is loaded before using it, but
	// for simplicity it is done here and enabling the live transcription is
	// tied to having said metadata.
	try {
		await liveTranscriptionStore.loadLiveTranscriptionLanguages()
	} catch (exception) {
		showError(t('spreed', 'Error when trying to load the available live transcription languages'))

		return false
	}

	try {
		await callViewStore.enableLiveTranscription(token.value)
	} catch (error) {
		showError(t('spreed', 'Failed to enable live transcription'))

		return false
	}

	return true
}

/**
 * Disable live transcriptions.
 */
async function disableLiveTranscription() {
	try {
		await callViewStore.disableLiveTranscription(token.value)
	} catch (error) {
		// Not being able to disable the live transcription is not really
		// relevant for the user, as the transcript will be no longer visible in
		// the UI anyway, so no error is shown in that case.
	}
}

/**
 * Enable live translations.
 *
 * Live transcriptions need to have been enabled first before enabling live
 * translations.
 */
async function enableLiveTranslation() {
	if (languageType.value === LanguageType.Target) {
		return
	}

	try {
		await callViewStore.setLiveTranscriptionTargetLanguage(token.value, targetLanguageId.value as string)
	} catch (error) {
		showError(t('spreed', 'Failed to enable live translations'))

		return
	}

	languageType.value = LanguageType.Target
}

/**
 * Disable live translations.
 *
 * This does not disable live transcriptions, so the transcription will continue
 * in the original language.
 */
async function disableLiveTranslation() {
	if (languageType.value === LanguageType.Original) {
		return
	}

	try {
		await callViewStore.setLiveTranscriptionTargetLanguage(token.value, null)
	} catch (error) {
		showError(t('spreed', 'Failed to disable live translations'))

		return
	}

	languageType.value = LanguageType.Original
}

/**
 * Switches the call view to the given layout.
 *
 * @param layout the layout to switch to
 */
function setCallLayout(layout: CallLayout) {
	callViewStore.setCallViewMode({
		token: token.value,
		isGrid: layout === CALL_LAYOUT.GRID,
		// Picking the grid says nothing about how speaker view should look, so
		// the preference is kept for when it is picked again
		isMultiSpeaker: layout === CALL_LAYOUT.GRID ? null : layout === CALL_LAYOUT.MULTI_SPEAKER,
		clearLast: false,
	})
	callViewStore.setSelectedVideoPeerId(null)
}
</script>

<template>
	<div ref="bottomBar" class="bottom-bar" data-theme-dark>
		<div v-if="!isSidebar" class="bottom-bar-call-controls">
			<!-- Fullscreen -->
			<NcButton
				v-if="!hidingList.fullscreen"
				:aria-label="fullscreenLabel"
				:variant="isFullscreen ? 'secondary' : 'tertiary'"
				:title="fullscreenLabel"
				@click="toggleFullscreen">
				<template #icon>
					<IconFullscreen v-if="!isFullscreen" :size="20" />
					<IconFullscreenExit v-else :size="20" />
				</template>
			</NcButton>
			<!-- Call layout switcher -->
			<NcActions
				v-if="showCallLayoutSwitch && !hidingList.callLayout"
				variant="tertiary"
				:aria-label="CALL_LAYOUT_COMPONENT[callLayout].label"
				:title="CALL_LAYOUT_COMPONENT[callLayout].label">
				<template #icon>
					<component :is="CALL_LAYOUT_COMPONENT[callLayout].icon" :size="20" />
				</template>
				<NcActionButton
					v-for="layout in callLayouts"
					:key="layout"
					:modelValue="callLayout"
					:value="layout"
					type="radio"
					closeAfterClick
					@click="setCallLayout(layout)">
					<template #icon>
						<component :is="CALL_LAYOUT_COMPONENT[layout].icon" :size="20" />
					</template>
					{{ CALL_LAYOUT_COMPONENT[layout].label }}
				</NcActionButton>
			</NcActions>
		</div>

		<div class="bottom-bar-call-controls">
			<!-- Local media controls -->
			<TopBarMediaControls
				:token="token"
				:model="localMediaModel"
				:isSidebar="isSidebar"
				:hideVirtualBackgroundShortcut="hidingList.virtualBackground"
				:localCallParticipantModel="localCallParticipantModel" />

			<!-- Reactions menu -->
			<ReactionMenu
				v-if="!isSidebar"
				:token="token"
				:localMediaModel="localMediaModel"
				:localCallParticipantModel="localCallParticipantModel" />

			<div
				v-if="!isSidebar && isLiveTranscriptionSupported && !hidingList.liveTranscription"
				class="live-transcription-button-wrapper">
				<NcButton
					:title="liveTranscriptionButtonLabel"
					:aria-label="liveTranscriptionButtonLabel"
					:variant="callViewStore.isLiveTranscriptionEnabled ? 'secondary' : 'tertiary'"
					:disabled="isLiveTranscriptionLoading"
					:class="{
						'translation-button': isLiveTranslationSupported || hintTranslationSupported,
					}"
					@click="toggleLiveTranscriptionAndTranslation">
					<template #icon>
						<NcLoadingIcon
							v-if="isLiveTranscriptionLoading"
							:size="20" />
						<IconSubtitles
							v-else-if="callViewStore.isLiveTranscriptionEnabled"
							:size="20" />
						<IconSubtitlesOutline
							v-else
							:size="20" />
					</template>
				</NcButton>

				<NcActions
					v-if="isLiveTranslationSupported"
					class="language-selector-button"
					@open="handleLiveTranscriptionLanguageSelectorOpen">
					<template #icon>
						<IconChevronUp :size="16" />
					</template>
					<NcActionButton
						class="language-selector__action"
						type="radio"
						:disabled="isLiveTranscriptionLoading"
						:modelValue="languageType"
						:value="LanguageType.Original"
						:title="originalLanguageButtonLabel"
						closeAfterClick
						@click="switchToOriginalLanguage">
						{{ originalLanguageButtonLabel }}
					</NcActionButton>
					<NcActionSeparator />
					<NcActionButton
						class="language-selector__action"
						type="radio"
						:disabled="isLiveTranscriptionLoading || !targetLanguageAvailable"
						:modelValue="languageType"
						:value="LanguageType.Target"
						:title="targetLanguageButtonLabel"
						closeAfterClick
						@click="switchToTargetLanguage">
						{{ targetLanguageButtonLabel }}
					</NcActionButton>
				</NcActions>
				<NcActions
					v-else-if="hintTranslationSupported"
					class="language-selector-button">
					<template #icon>
						<IconChevronUp :size="16" />
					</template>
					<NcActionButton
						class="language-selector__action"
						type="radio"
						:modelValue="languageType"
						:value="LanguageType.Original"
						:title="hintOriginalButtonLabel">
						{{ hintOriginalButtonLabel }}
					</NcActionButton>
					<NcActionSeparator />
					<NcActionButton
						class="language-selector__action"
						type="radio"
						disabled
						:modelValue="languageType"
						:value="LanguageType.Target"
						:title="hintTranslationButtonLabel">
						{{ hintTranslationButtonLabel }}
					</NcActionButton>
				</NcActions>
			</div>
			<div
				v-else-if="!isSidebar && hintTranscriptionSupported"
				class="live-transcription-button-wrapper">
				<NcButton
					:title="hintTranscriptionButtonLabel"
					:aria-label="hintTranscriptionButtonLabel"
					variant="secondary"
					disabled>
					<template #icon>
						<IconSubtitlesOutline
							:size="20" />
					</template>
				</NcButton>
			</div>
		</div>
		<div ref="callButtonWithActions" class="bottom-bar-options call-options">
			<!-- Collapsed actions -->
			<NcActions v-if="hasHiddenItems" forceMenu>
				<!-- Fullscreen -->
				<NcActionButton
					v-if="!isSidebar && hidingList.fullscreen"
					:aria-label="fullscreenLabel"
					:variant="isFullscreen ? 'secondary' : 'tertiary'"
					:title="fullscreenLabel"
					@click="toggleFullscreen">
					<template #icon>
						<IconFullscreen v-if="!isFullscreen" :size="20" />
						<IconFullscreenExit v-else :size="20" />
					</template>
					{{ fullscreenLabel }}
				</NcActionButton>
				<!-- Call layout switcher -->
				<template v-if="hidingList.callLayout && showCallLayoutSwitch">
					<NcActionButton
						v-for="layout in callLayouts"
						:key="layout"
						:modelValue="callLayout"
						:value="layout"
						type="radio"
						closeAfterClick
						@click="setCallLayout(layout)">
						<template #icon>
							<component :is="CALL_LAYOUT_COMPONENT[layout].icon" :size="20" />
						</template>
						{{ CALL_LAYOUT_COMPONENT[layout].label }}
					</NcActionButton>
				</template>
				<NcActionButton
					v-if="isLiveTranscriptionSupported && hidingList.liveTranscription"
					:title="liveTranscriptionButtonLabel"
					:aria-label="liveTranscriptionButtonLabel"
					:variant="(callViewStore.isLiveTranscriptionEnabled && languageType === LanguageType.Target) ? 'secondary' : 'tertiary'"
					:disabled="isLiveTranscriptionLoading"
					@click="toggleLiveTranscription">
					<template #icon>
						<NcLoadingIcon
							v-if="isLiveTranscriptionLoading && languageType === LanguageType.Original"
							:size="20" />
						<IconSubtitles
							v-else-if="callViewStore.isLiveTranscriptionEnabled && languageType === LanguageType.Original"
							:size="20" />
						<IconSubtitlesOutline
							v-else
							:size="20" />
					</template>
					{{ liveTranscriptionButtonLabel }}
				</NcActionButton>
				<NcActionButton
					v-if="isLiveTranslationSupported && hidingList.liveTranscription"
					:title="liveTranslationButtonLabel"
					:aria-label="liveTranslationButtonLabel"
					:variant="(callViewStore.isLiveTranscriptionEnabled && languageType === LanguageType.Target) ? 'secondary' : 'tertiary'"
					:disabled="isLiveTranscriptionLoading || !targetLanguageAvailable"
					@click="toggleLiveTranslation">
					<template #icon>
						<NcLoadingIcon
							v-if="isLiveTranscriptionLoading && languageType === LanguageType.Target"
							:size="20" />
						<IconSubtitles
							v-else-if="callViewStore.isLiveTranscriptionEnabled && languageType === LanguageType.Target"
							:size="20" />
						<IconSubtitlesOutline
							v-else
							:size="20" />
					</template>
					{{ liveTranslationButtonLabel }}
				</NcActionButton>
			</NcActions>

			<CallEndLeaveButton
				v-show="!(isVoiceRoom && isGuestActor)"
				class="call-button"
				shrinkOnMobile
				:hideText="isSidebar"
				:tertiary="!!localMediaModel.attributes.localScreen" />
		</div>
	</div>
</template>

<style lang="scss" scoped>
.bottom-bar {
	position: absolute;
	bottom: 0;
	inset-inline: 0;
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: var(--wrapper-padding);
	z-index: 10;
	overflow: hidden;

	:deep(.button-vue--tertiary) {
		background-color: var(--color-primary-light);
	}
}

.bottom-bar-call-controls {
	display: flex;
	align-items: center;
	flex-direction: row;
	gap: var(--default-grid-baseline);
}

.bottom-bar-call-controls:not(:has(*)) {
	display: none
}

.call-options {
	display: flex;
	align-items: center;
	flex-direction: row;
	gap: 4px;
}

.live-transcription-button-wrapper {
	display: flex;
	align-items: center;
	gap: 1px;

	// Overwriting NcButton styles
	.translation-button {
		border-start-end-radius: 2px;
		border-end-end-radius: 2px;
	}
}

.language-selector-button :deep(.action-item__menutoggle) {
	--button-size: var(--clickable-area-small);
	height: var(--default-clickable-area);
	border-start-start-radius: 2px;
	border-end-start-radius: 2px;
}

.language-selector__action {
	// Overwriting NcActionButton styles
	:deep(.action-button__longtext) {
		display: -webkit-box;
		-webkit-line-clamp: 1;
		-webkit-box-orient: vertical;
		overflow: hidden;
		text-overflow: ellipsis;
		padding: 0;
		max-width: 350px;
	}

	:deep(.action-button__longtext-wrapper) {
		max-width: 350px;
	}

	:deep(.action-button__icon) {
		width: 0;
		margin-inline-start: calc(var(--default-grid-baseline) * 3);
	}

	:deep(.action-button > span) {
		height: var(--default-clickable-area);
	}
}
</style>
